import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { formatFullMonth } from 'util/formatAgo';
import classNames from 'classnames';
import { SnowUnit } from 'components/blocks/Unit';
import { ISnowfallItem } from 'api/resort/types';
import { parseDate } from 'util/formatDate';
import { useMemo } from 'react';
import styles from './styles.module.scss';

export interface IPropsSnowfallRow {
  snowfall: ISnowfallItem;
  className: string;
  average?: boolean;
  annual?: boolean;
  isLastRow?: boolean;
}

export const HistoryWeatherRowHeader = ({ average }: { average?: boolean }) => {
  const { t } = useOTSTranslation('common');
  return (
    <thead>
      <tr className={styles.header}>
        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
        <th />
        <th className={styles.totalSnowfall}>
          <span
            className={classNames('h5', styles.h5)}
            title={t(average ? 'resort.overview.averageSnowfall' : 'resort.historicalSnowfall.totalSnowfall')}
          >
            {t(average ? 'resort.overview.averageSnowfall' : 'resort.historicalSnowfall.totalSnowfall')}
          </span>
        </th>
        <th className={styles.snowfallDay}>
          <span className={classNames('h5', styles.h5)} title={t('resort.historicalSnowfall.snowfallDay')}>
            {t('resort.historicalSnowfall.snowfallDay')}
          </span>
        </th>
        <th className={styles.baseDepth}>
          <span className={classNames('h5', styles.h5)} title={t('resort.historicalSnowfall.avgBaseDepth')}>
            {t('resort.historicalSnowfall.avgBaseDepth')}
          </span>
        </th>
        <th className={styles.maxBase}>
          <span className={classNames('h5', styles.h5)} title={t('resort.historicalSnowfall.maxBaseDepth')}>
            {t('resort.historicalSnowfall.maxBaseDepth')}
          </span>
        </th>
        <th className={styles.biggest}>
          <span className={classNames('h5', styles.h5)} title={t('resort.historicalSnowfall.biggestSnowfall')}>
            {t('resort.historicalSnowfall.biggestSnowfall')}
          </span>
        </th>
      </tr>
    </thead>
  );
};

export const HistoryWeatherRow = (
  {
    snowfall, className, average, annual, isLastRow,
  }: IPropsSnowfallRow,
) => {
  const { t } = useOTSTranslation('common');

  const showAges = useMemo(() => (date: string) => {
    const { year } = parseDate(date);

    if (average) { return t('resort.historicalSnowfall.average'); }
    if (isLastRow) { return `${year} - ${t('resort.historicalSnowfall.toDate')}`; }

    return `${year} - ${year + 1}`;
  }, [isLastRow, average]);

  const pluralDays = !(snowfall.snowDays === 1);

  const snowfallDays = t(pluralDays ? 'common.timeDay_plural' : 'common.timeDay', { count: snowfall.snowDays });

  return (
    <tr className={classNames(styles.row, className)}>
      <th scope="row" className={classNames(styles.rowTitle)}>
        <span className={classNames('h4', styles.h4, styles.title)}>
          {annual ? showAges(snowfall.date) : formatFullMonth(snowfall.date)}
        </span>
      </th>
      <td className={styles.totalSnowfall}>
        <span className={classNames('h4', styles.h4)}><SnowUnit values={snowfall.totalSnow} /></span>
      </td>
      <td className={styles.snowfallDay}>
        <span className={classNames('h4', styles.h4)}>
          {snowfallDays}
        </span>
      </td>
      <td className={styles.baseDepth}>
        <span className={classNames('h4', styles.h4)}><SnowUnit values={snowfall.baseDepth} /></span>
      </td>
      <td className={styles.maxBase}>
        <span className={classNames('h4', styles.h4)}><SnowUnit values={snowfall.maxBaseDepth} /></span>
      </td>
      <td className={styles.biggest}>
        <span className={classNames('h4', styles.h4)}><SnowUnit values={snowfall.biggestSnowfall} /></span>
      </td>
    </tr>
  );
};
